import React, { useEffect, useState } from "react";
import { AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import RateChangeLog from "./RateChangeLog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const monthLabel = (month) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

/** Month-by-month view of which fee rate was in force, with overlap and gap warnings. */
export default function RateHistory({ employers }) {
  const [company, setCompany] = useState("");
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!company && employers.length) setCompany(employers[0]);
  }, [employers, company]);

  useEffect(() => {
    if (!company) return;
    setLoading(true);
    api.get("/admin/rates/history", { params: { company } })
      .then(({ data }) => setHistory(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [company]);

  const types = history?.rate_types || [];

  return (
    <div data-testid="rate-history">
      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div className="min-w-[220px]">
          <label className="field-label">Employer</label>
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="history-company">
              <SelectValue placeholder="Select employer"/>
            </SelectTrigger>
            <SelectContent className="!rounded-none max-h-72">
              {employers.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs max-w-md" style={{ color: "var(--muted)" }}>
          Each row is a month on file. Cells show the rate in force; the latest effective date wins
          where windows overlap.
        </p>
      </div>

      {!!history?.warnings?.length && (
        <div className="border p-5 mb-5 space-y-2" style={{ borderColor: "var(--warn, #B45309)", background: "rgba(180,83,9,0.06)" }} data-testid="history-warnings">
          {history.warnings.map((warning, index) => (
            <div key={index} className="flex items-start gap-2 text-sm" data-testid={`history-warning-${warning.kind}-${warning.rate_type}`}>
              <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: "#B45309" }}/>
              <span style={{ color: "var(--ink)" }}>
                <span className="font-bold">{warning.kind === "overlap" ? "Overlap" : "Gap"} — {warning.rate_type}:</span>{" "}
                {warning.detail} <span style={{ color: "var(--muted)" }}>
                  ({warning.months.slice(0, 6).map(monthLabel).join(", ")}
                  {warning.months.length > 6 ? `, +${warning.months.length - 6} more` : ""})
                </span>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <table className="w-full text-sm min-w-[720px]" data-testid="rate-history-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Month</th>
              {types.map((type) => <th key={type} className="px-4 py-3 text-right">{type}</th>)}
            </tr>
          </thead>
          <tbody>
            {(history?.rows || []).map((row) => (
              <tr key={row.month} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`history-row-${row.month}`}>
                <td className="px-4 py-2.5 font-medium" style={{ color: "var(--ink)" }}>{monthLabel(row.month)}</td>
                {types.map((type) => {
                  const cell = row.cells[type] || {};
                  return (
                    <td key={type} className="px-4 py-2.5 text-right" data-testid={`history-cell-${row.month}-${type}`}>
                      {cell.missing ? (
                        <span className="text-xs" style={{ color: "#B45309" }}>no rate</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 font-heading font-bold" style={{ color: "var(--ink)" }} title={`${cell.start_date} → ${cell.end_date}${cell.notes ? ` · ${cell.notes}` : ""}`}>
                          {cell.overlap && <Layers size={11} style={{ color: "#B45309" }}/>}
                          {(cell.rate_percent * 100).toFixed(2)}%
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!loading && !(history?.rows || []).length && (
              <tr><td colSpan={types.length + 1} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="history-empty">
                No rate history for this employer yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border mt-8 p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="recent-rate-changes">
        <h3 className="font-heading font-bold text-lg mb-1" style={{ color: "var(--ink)" }}>Recent rate changes</h3>
        <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>
          Every change for this employer, including rates since deleted and refreshes from the monthly workbook.
        </p>
        {company && <RateChangeLog company={company} limit={50}
          emptyNote="No rate changes recorded for this employer yet."/>}
      </div>
    </div>
  );
}
